import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatMoney } from '@/utils/format';

/**
 * The only way money is rendered.
 *
 * Every amount in Trinity is an integer of minor units with a currency beside it, and
 * this is the one place that pair becomes text. Centralising it is what guarantees a
 * report, a table cell, a receipt and a dashboard tile all agree — and that a negative
 * survives being printed in black and white, because it is parenthesised as well as
 * coloured.
 *
 * Reference: documentation/architecture/widget-architecture.md §4.6.
 */

export interface MoneyDisplayProps {
  /** Integer minor units. */
  amount: number;
  currency: string;
  /** Colours negatives red and positives green. Off by default — most amounts are neutral. */
  colorBySign?: boolean;
  /** Colours negatives red without turning positives green. */
  colorNegative?: boolean;
  showSymbol?: boolean;
  /**
   * Defaults to parenthesised negatives (print-safe). Set false for a leading minus —
   * useful for balances where "in the red" should read as a signed amount.
   */
  parenthesiseNegative?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZES = {
  sm: 'text-body-sm',
  md: 'text-body',
  lg: 'text-section-title font-semibold',
  xl: 'text-display font-semibold',
} as const;

export const MoneyDisplay = ({
  amount,
  currency,
  colorBySign = false,
  colorNegative = false,
  showSymbol = true,
  parenthesiseNegative = true,
  size = 'md',
  className,
}: MoneyDisplayProps) => {
  const locale = usePreferences((state) => state.locale);

  return (
    <span
      className={cn(
        'font-numeric tabular-nums font-semibold whitespace-nowrap text-fg',
        SIZES[size],
        // Emphasis tokens — dark enough for plain surfaces. -fg is for badges.
        (colorBySign || colorNegative) && amount < 0 && 'text-danger-emphasis',
        colorBySign && amount > 0 && 'text-success-emphasis',
        className,
      )}
    >
      {formatMoney(amount, currency, { locale, showSymbol, parenthesiseNegative })}
    </span>
  );
};
